/* PlanTimelineSection — Phase B Schedule-tab UI for the new Doctor
 * Self-Plan timeline fields (iter97.38).
 *
 * Renders inside the `wear` section of PlanEditorInline (and the planner
 * workspace) so both the doctor self-plan and CoE-planner authoring
 * surfaces share the same UI. Fields:
 *
 *   - start_date                   doctor picks the first-aligner delivery date
 *   - eta (derived, read-only)     auto-computed from totalStages × wear + pauses
 *   - milestones[]                 doctor adds chair-side review checkpoints
 *   - refinement_expected          doctor flags upfront they expect a refinement round
 *   - refinement_expected_reason   reason dropdown
 *   - phase_breaks[]               doctor defines treatment phases as stage ranges
 *
 * All inputs use the same controlled-input pattern as PlanEditorInline so
 * auto-save still flows through the parent's debounce. Dual retention
 * recall lives inside the retention section (separate component below for
 * reuse only here — it's tiny).
 */
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
    CalendarBlank, Clock, Flag, Path, Plus, Trash, ArrowsClockwise, Lightning,
} from "@phosphor-icons/react";
import { computeETA, fmtETA, defaultMilestonesForPlan } from "@/lib/planTimeline";

const REFINEMENT_REASONS = [
    { value: "", label: "— select reason (optional) —" },
    { value: "extraction", label: "Extraction case" },
    { value: "class_iii", label: "Class III malocclusion" },
    { value: "open_bite", label: "Open bite" },
    { value: "deep_bite", label: "Deep bite" },
    { value: "growth", label: "Skeletal growth / paediatric" },
    { value: "other", label: "Other" },
];

export default function PlanTimelineSection({ plan, onChange, readOnly = false }) {
    const eta = computeETA(plan);
    const milestones = Array.isArray(plan.milestones) ? plan.milestones : [];
    const phases = Array.isArray(plan.phase_breaks) ? plan.phase_breaks : [];

    const updMilestones = (next) => onChange("milestones", next);
    const updPhases = (next) => onChange("phase_breaks", next);

    const addMilestone = () => {
        // First milestone seeds a suggested mid-treatment review if there's
        // enough plan info to derive one. Otherwise it's an empty row.
        const suggested = milestones.length === 0 ? defaultMilestonesForPlan(plan) : [];
        const next = suggested.length
            ? suggested
            : [...milestones, { stage: "", label: "" }];
        updMilestones(next.length > milestones.length ? next : [...milestones, { stage: "", label: "" }]);
    };
    const editMilestone = (idx, key, value) => {
        const next = milestones.slice();
        next[idx] = { ...next[idx], [key]: key === "stage" ? (value === "" ? "" : Number(value)) : value };
        updMilestones(next);
    };
    const removeMilestone = (idx) => updMilestones(milestones.filter((_, i) => i !== idx));

    const addPhase = () => {
        // Suggest next available phase starting after the last phase end
        // (or stage 1) and running 6 stages ahead.
        const last = phases[phases.length - 1];
        const totalStages = Math.max(Number(plan.aligner_count_upper || 0), Number(plan.aligner_count_lower || 0));
        const from = last ? Number(last.to_stage || 0) + 1 : 1;
        const to = totalStages > 0 ? Math.min(totalStages, from + 5) : from + 5;
        updPhases([...phases, { from_stage: from, to_stage: to, pause_days: 14, label: "" }]);
    };
    const editPhase = (idx, key, value) => {
        const next = phases.slice();
        const coerced = ["from_stage", "to_stage", "pause_days"].includes(key)
            ? (value === "" ? "" : Number(value))
            : value;
        next[idx] = { ...next[idx], [key]: coerced };
        updPhases(next);
    };
    const removePhase = (idx) => updPhases(phases.filter((_, i) => i !== idx));

    return (
        <div className="mt-5 pt-4 border-t border-border/60 space-y-5" data-testid="plan-timeline-section">
            {/* iter98.70 — Removed by user request: the "Treatment timeline"
                header + "Treatment start date" + "Projected completion (auto)"
                + "Chair-side milestones" sub-blocks. These were
                doctor-facing chair-side scheduling concerns; the
                planner shouldn't be authoring them. Refinement
                expectations + Phase breaks (below) stay — those are
                clinical authoring decisions the planner owns. */}

            {/* Refinement expected */}
            <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-3 space-y-3">
                <label
                    className="flex items-start gap-2.5 cursor-pointer"
                    data-testid="plan-refinement-expected-row"
                >
                    <Checkbox
                        checked={!!plan.refinement_expected}
                        onCheckedChange={(v) => onChange("refinement_expected", v === true)}
                        className="mt-0.5"
                        data-testid="plan-refinement-expected"
                        disabled={readOnly}
                    />
                    <span className="select-none">
                        <span className="text-sm font-semibold text-violet-900 flex items-center gap-1.5">
                            <ArrowsClockwise size={13} weight="duotone" /> I expect this case will need a refinement round
                        </span>
                        <span className="text-[11px] text-violet-900/75 block leading-snug mt-0.5">
                            Sets expectations with the lab + locks in refinement capacity. You can still skip it later.
                        </span>
                    </span>
                </label>
                {plan.refinement_expected && (
                    <div>
                        <Label className="text-[10px] font-semibold uppercase tracking-wide text-violet-900/80">
                            Reason (optional)
                        </Label>
                        <select
                            value={plan.refinement_expected_reason || ""}
                            onChange={(e) => onChange("refinement_expected_reason", e.target.value)}
                            className="mt-1 h-9 w-full rounded-md border border-input bg-white px-2 text-sm"
                            data-testid="plan-refinement-reason"
                            disabled={readOnly}
                        >
                            {REFINEMENT_REASONS.map((r) => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Phase breaks */}
            <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-1.5">
                        <Path size={12} className="text-primary" />
                        <Label className="text-[11px] font-semibold">Phase breaks</Label>
                        <span className="text-[10px] text-muted-foreground">({phases.length})</span>
                    </div>
                    {!readOnly && (
                        <Button
                            type="button" size="sm" variant="outline"
                            onClick={addPhase}
                            className="h-7 px-2 text-[11px]"
                            data-testid="plan-add-phase"
                        >
                            <Plus size={11} weight="bold" className="mr-1" /> Add phase
                        </Button>
                    )}
                </div>
                {phases.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground italic">
                        No phase breaks. Add one to split treatment into ranges with a planned pause between them
                        (e.g. allow teeth to settle before the second half).
                    </p>
                ) : (
                    <div className="space-y-2">
                        {phases.map((p, i) => (
                            <div
                                key={i}
                                className="rounded-lg border border-border bg-white p-2.5 grid grid-cols-1 sm:grid-cols-[80px_80px_90px_1fr_auto] gap-2 items-start"
                                data-testid={`plan-phase-row-${i}`}
                            >
                                <div>
                                    <Label className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">From</Label>
                                    <Input
                                        type="number" min="1"
                                        value={p.from_stage ?? ""}
                                        onChange={(e) => editPhase(i, "from_stage", e.target.value)}
                                        className="h-8 mt-1 text-xs"
                                        data-testid={`plan-phase-from-${i}`}
                                        disabled={readOnly}
                                    />
                                </div>
                                <div>
                                    <Label className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">To</Label>
                                    <Input
                                        type="number" min="1"
                                        value={p.to_stage ?? ""}
                                        onChange={(e) => editPhase(i, "to_stage", e.target.value)}
                                        className="h-8 mt-1 text-xs"
                                        data-testid={`plan-phase-to-${i}`}
                                        disabled={readOnly}
                                    />
                                </div>
                                <div>
                                    <Label className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Pause (days)</Label>
                                    <Input
                                        type="number" min="0"
                                        value={p.pause_days ?? ""}
                                        onChange={(e) => editPhase(i, "pause_days", e.target.value)}
                                        className="h-8 mt-1 text-xs"
                                        data-testid={`plan-phase-pause-${i}`}
                                        disabled={readOnly}
                                    />
                                </div>
                                <div>
                                    <Label className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Label (optional)</Label>
                                    <Input
                                        type="text"
                                        value={p.label || ""}
                                        onChange={(e) => editPhase(i, "label", e.target.value)}
                                        placeholder="e.g. Phase 1 — pre-IPR"
                                        className="h-8 mt-1 text-xs"
                                        data-testid={`plan-phase-label-${i}`}
                                        disabled={readOnly}
                                    />
                                </div>
                                {!readOnly && (
                                    <button
                                        type="button"
                                        onClick={() => removePhase(i)}
                                        className="h-8 px-2 mt-5 text-rose-600 hover:bg-rose-50 rounded-md inline-flex items-center"
                                        aria-label="Remove phase"
                                        data-testid={`plan-phase-remove-${i}`}
                                    >
                                        <Trash size={13} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

/* RetentionRecallPair — dual retention recall input (Phase B). Replaces
   the legacy single-recall input. When the user has a legacy
   retention_recall_months value but no first/second, we auto-migrate it
   to the first slot via the onChange seed in PlanEditorInline. */
export function RetentionRecallPair({ plan, onChange, readOnly = false, fieldLabel }) {
    const first = plan.retention_recall_first_months ?? plan.retention_recall_months ?? "";
    const second = plan.retention_recall_second_months ?? "";
    return (
        <div className="grid grid-cols-2 gap-3">
            <div>
                <Label className="text-[11px] font-semibold">
                    {fieldLabel ? fieldLabel("retention_recall_first_months", "First recall (months)") : "First recall (months)"}
                </Label>
                <Input
                    type="number" min="0" step="1"
                    value={first}
                    onChange={(e) => onChange("retention_recall_first_months", e.target.value)}
                    placeholder="3"
                    className="h-9 mt-1 text-sm"
                    data-testid="plan-retention-recall-first"
                    disabled={readOnly}
                />
            </div>
            <div>
                <Label className="text-[11px] font-semibold">Second recall (months)</Label>
                <Input
                    type="number" min="0" step="1"
                    value={second}
                    onChange={(e) => onChange("retention_recall_second_months", e.target.value)}
                    placeholder="12"
                    className="h-9 mt-1 text-sm"
                    data-testid="plan-retention-recall-second"
                    disabled={readOnly}
                />
            </div>
        </div>
    );
}
